import React from 'react';
import {
  Box,
  Typography,
  Button,
  Chip,
  Card,
  CardContent,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Tooltip,
  ToggleButtonGroup,
  ToggleButton,
  Radio,
  RadioGroup,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

// ── Reusable theme-aware style helpers ──────────────────────────────
const BG_API = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(237, 108, 2, 0.15)'
    : 'rgba(237, 108, 2, 0.06)';
const BG_MANUAL = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(2, 136, 209, 0.15)'
    : 'rgba(2, 136, 209, 0.06)';
const BG_API_CARD = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(237, 108, 2, 0.12)'
    : 'rgba(237, 108, 2, 0.04)';
const BG_MANUAL_CARD = (t) =>
  t.palette.mode === 'dark'
    ? 'rgba(2, 136, 209, 0.12)'
    : 'rgba(2, 136, 209, 0.04)';

const SX_BOLD_API = { fontWeight: 'bold', color: 'warning.main' };
const SX_BOLD_MANUAL = { fontWeight: 'bold', color: 'info.main' };
const SX_RADIO_API = {
  p: 0.5,
  color: 'warning.main',
  '&.Mui-checked': { color: 'warning.main' },
};
const SX_RADIO_MANUAL = {
  p: 0.5,
  color: 'info.main',
  '&.Mui-checked': { color: 'info.main' },
};
const SX_COMPACT_BTN = { fontSize: '0.7rem', py: 0.25, px: 1 };

/**
 * Displays the field-by-field diff between a staged voyage and a manual candidate.
 * Supports "Take All" and "Selective Merge" modes.
 */
const StagingDiffViewer = ({
  selectedCandidate,
  diffData,
  mergeMode,
  fieldSelections,
  shipmentSelections,
  shipmentFieldSelections,
  hasAnySelection,
  selectionSummary,
  onFieldSelectionChange,
  onShipmentSelectionChange,
  onShipmentFieldSelectionChange,
  onSetMergeMode,
  onResetSelections,
  onGoBack,
}) => {
  if (!diffData || !selectedCandidate) return null;

  return (
    <>
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
        <Button startIcon={<ArrowBackIcon />} onClick={onGoBack} size='small'>
          Back to Candidates
        </Button>
        <Typography variant='subtitle1' fontWeight='bold'>
          Comparing with:{' '}
          {selectedCandidate.voyageNumber || selectedCandidate.voyageId}
        </Typography>
      </Box>

      {/* Merge Mode Toggle Bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 2,
          mb: 2,
          p: 1.5,
          bgcolor: 'action.hover',
          borderRadius: 1,
          border: '1px solid',
          borderColor: 'divider',
        }}
      >
        <ToggleButtonGroup
          value={mergeMode}
          exclusive
          size='small'
          onChange={(_, val) => val && onSetMergeMode(val)}
        >
          <ToggleButton value='takeAll' disabled={hasAnySelection}>
            Take All from API
          </ToggleButton>
          <ToggleButton value='selective'>Selective Merge</ToggleButton>
        </ToggleButtonGroup>
        {hasAnySelection && (
          <Tooltip title='Reset all selections to default'>
            <IconButton size='small' onClick={onResetSelections}>
              <RestartAltIcon fontSize='small' />
            </IconButton>
          </Tooltip>
        )}
        {mergeMode === 'selective' && (
          <Chip
            size='small'
            label={`Taking ${selectionSummary.takingFromApi} from API, keeping ${selectionSummary.keepingManual} from Manual${selectionSummary.excluding ? `, excluding ${selectionSummary.excluding}` : ''}`}
            color='info'
            variant='outlined'
          />
        )}
      </Box>

      {/* Voyage Field Diffs */}
      <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 1 }}>
        Voyage Fields
      </Typography>
      <TableContainer component={Paper} variant='outlined' sx={{ mb: 3 }}>
        <Table size='small' sx={{ tableLayout: 'fixed' }}>
          <TableHead>
            <TableRow>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: mergeMode === 'selective' ? '22%' : '25%',
                }}
              >
                Field
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: mergeMode === 'selective' ? '24%' : '30%',
                }}
              >
                API Value
              </TableCell>
              <TableCell
                sx={{
                  fontWeight: 'bold',
                  width: mergeMode === 'selective' ? '24%' : '30%',
                }}
              >
                Manual Value
              </TableCell>
              <TableCell
                align='center'
                sx={{
                  fontWeight: 'bold',
                  width: mergeMode === 'selective' ? '8%' : '15%',
                }}
              >
                Status
              </TableCell>
              {mergeMode === 'selective' && (
                <TableCell
                  align='center'
                  sx={{ fontWeight: 'bold', width: '22%' }}
                >
                  Source
                </TableCell>
              )}
            </TableRow>
          </TableHead>
          <TableBody>
            {diffData.voyageFieldDiffs?.map((diff, i) => {
              const isSelectable = diff.different && mergeMode === 'selective';
              const rawSelection = fieldSelections[diff.fieldName];
              const selection = isSelectable
                ? rawSelection || 'manual'
                : rawSelection;
              const rowBg = !diff.different
                ? 'inherit'
                : selection === 'api'
                  ? BG_API
                  : mergeMode === 'selective' && diff.different
                    ? BG_MANUAL
                    : 'inherit';
              return (
                <TableRow
                  key={i}
                  sx={{
                    bgcolor: rowBg,
                    opacity:
                      !diff.different && mergeMode === 'selective' ? 0.5 : 1,
                  }}
                >
                  <TableCell>{diff.fieldName}</TableCell>
                  <TableCell sx={selection === 'api' ? SX_BOLD_API : {}}>
                    {diff.apiValue ?? '-'}
                  </TableCell>
                  <TableCell sx={selection === 'manual' ? SX_BOLD_MANUAL : {}}>
                    {diff.manualValue ?? '-'}
                  </TableCell>
                  <TableCell align='center'>
                    {diff.different ? (
                      <WarningAmberIcon color='warning' fontSize='small' />
                    ) : (
                      <CheckCircleIcon color='success' fontSize='small' />
                    )}
                  </TableCell>
                  {mergeMode === 'selective' && (
                    <TableCell align='center'>
                      {isSelectable ? (
                        <RadioGroup
                          row
                          value={selection || 'manual'}
                          onChange={(e) =>
                            onFieldSelectionChange(
                              diff.fieldName,
                              e.target.value
                            )
                          }
                          sx={{ justifyContent: 'center' }}
                        >
                          <FormControlLabel
                            value='api'
                            control={<Radio size='small' sx={SX_RADIO_API} />}
                            label={
                              <Typography variant='caption'>API</Typography>
                            }
                            sx={{ mr: 1 }}
                          />
                          <FormControlLabel
                            value='manual'
                            control={
                              <Radio size='small' sx={SX_RADIO_MANUAL} />
                            }
                            label={
                              <Typography variant='caption'>Manual</Typography>
                            }
                          />
                        </RadioGroup>
                      ) : (
                        <Typography variant='caption' color='text.disabled'>
                          Same
                        </Typography>
                      )}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Shipment Diffs */}
      {diffData.shipmentDiffs && diffData.shipmentDiffs.length > 0 && (
        <>
          <Typography variant='subtitle2' fontWeight='bold' sx={{ mb: 1 }}>
            Shipment Comparison
          </Typography>
          {diffData.shipmentDiffs.map((group, gi) => (
            <ShipmentDiffCard
              key={gi}
              group={group}
              mergeMode={mergeMode}
              shipmentSelections={shipmentSelections}
              shipmentFieldSelections={shipmentFieldSelections}
              onShipmentSelectionChange={onShipmentSelectionChange}
              onShipmentFieldSelectionChange={onShipmentFieldSelectionChange}
            />
          ))}
        </>
      )}
    </>
  );
};

/**
 * A single shipment market diff card.
 */
const ShipmentDiffCard = ({
  group,
  mergeMode,
  shipmentSelections,
  shipmentFieldSelections,
  onShipmentSelectionChange,
  onShipmentFieldSelectionChange,
}) => {
  const marketId = group.marketId;
  const rawShipSel = shipmentSelections[marketId];
  const shipSel =
    mergeMode === 'selective'
      ? rawShipSel || (group.apiOnly ? 'INCLUDE_NEW' : 'KEEP_MANUAL')
      : rawShipSel;
  const borderColor = group.apiOnly
    ? '#ed6c02'
    : group.manualOnly
      ? '#0288d1'
      : 'transparent';
  const cardBg =
    mergeMode !== 'selective'
      ? 'inherit'
      : shipSel === 'TAKE_API' || shipSel === 'INCLUDE_NEW'
        ? BG_API_CARD
        : shipSel === 'KEEP_MANUAL'
          ? BG_MANUAL_CARD
          : shipSel === 'EXCLUDE'
            ? 'action.disabledBackground'
            : 'inherit';

  return (
    <Card
      variant='outlined'
      sx={{ mb: 2, borderLeft: `3px solid ${borderColor}`, bgcolor: cardBg }}
    >
      <CardContent>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <Typography variant='body2' fontWeight='bold'>
            {group.marketName}
          </Typography>
          {group.marketId && (
            <Typography variant='body2' color='text.secondary'>
              ({group.marketId})
            </Typography>
          )}
          {group.countryName && (
            <Typography variant='body2' color='text.secondary'>
              — {group.countryName}
            </Typography>
          )}
          {group.apiOnly && (
            <Chip label='API Only' size='small' color='warning' />
          )}
          {group.manualOnly && (
            <Chip label='Manual Only' size='small' color='info' />
          )}

          {/* Shipment selection controls */}
          {mergeMode === 'selective' && !group.manualOnly && !group.apiOnly && (
            <Box
              sx={{
                ml: 'auto',
                display: 'flex',
                gap: 0.5,
                alignItems: 'center',
              }}
            >
              <Tooltip title='Apply to all fields in this market'>
                <ToggleButtonGroup
                  value={
                    !shipmentFieldSelections[marketId]
                      ? shipSel || 'KEEP_MANUAL'
                      : null
                  }
                  exclusive
                  size='small'
                  onChange={(_, val) =>
                    val && onShipmentSelectionChange(marketId, val)
                  }
                >
                  <ToggleButton value='TAKE_API' sx={SX_COMPACT_BTN}>
                    All API
                  </ToggleButton>
                  <ToggleButton value='KEEP_MANUAL' sx={SX_COMPACT_BTN}>
                    All Manual
                  </ToggleButton>
                </ToggleButtonGroup>
              </Tooltip>
            </Box>
          )}
          {mergeMode === 'selective' && group.apiOnly && (
            <Box sx={{ ml: 'auto' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={shipSel !== 'EXCLUDE'}
                    onChange={(e) =>
                      onShipmentSelectionChange(
                        marketId,
                        e.target.checked ? 'INCLUDE_NEW' : 'EXCLUDE'
                      )
                    }
                  />
                }
                label={<Typography variant='caption'>Include</Typography>}
              />
            </Box>
          )}
          {mergeMode === 'selective' && group.manualOnly && (
            <Box sx={{ ml: 'auto' }}>
              <FormControlLabel
                control={
                  <Checkbox
                    size='small'
                    checked={shipSel !== 'EXCLUDE'}
                    onChange={(e) =>
                      onShipmentSelectionChange(
                        marketId,
                        e.target.checked ? 'KEEP_MANUAL' : 'EXCLUDE'
                      )
                    }
                  />
                }
                label={<Typography variant='caption'>Keep</Typography>}
              />
            </Box>
          )}
        </Box>

        <Table size='small' sx={{ tableLayout: 'fixed' }}>
          <TableBody>
            {group.fieldDiffs?.map((diff, i) => {
              const isShared = !group.apiOnly && !group.manualOnly;
              const showSource = mergeMode === 'selective';
              const perFieldMap = shipmentFieldSelections[marketId];
              const hasPerField =
                isShared && perFieldMap && Object.keys(perFieldMap).length > 0;

              let fieldSel;
              if (isShared && mergeMode === 'selective') {
                if (hasPerField && diff.different) {
                  fieldSel = perFieldMap[diff.fieldName] || 'manual';
                } else if (shipSel === 'TAKE_API') {
                  fieldSel = 'api';
                } else {
                  fieldSel = 'manual';
                }
              }
              const isFieldSelectable =
                isShared && mergeMode === 'selective' && diff.different;
              const rowBg = !isFieldSelectable
                ? 'inherit'
                : fieldSel === 'api'
                  ? BG_API
                  : BG_MANUAL;

              return (
                <TableRow
                  key={i}
                  sx={{
                    opacity:
                      shipSel === 'EXCLUDE'
                        ? 0.4
                        : !diff.different &&
                            isShared &&
                            mergeMode === 'selective'
                          ? 0.5
                          : 1,
                    bgcolor: rowBg,
                  }}
                >
                  <TableCell sx={{ width: showSource ? '22%' : '25%' }}>
                    {diff.fieldName}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(mergeMode === 'selective' &&
                      (fieldSel === 'api' ||
                        (!isShared &&
                          (shipSel === 'TAKE_API' ||
                            shipSel === 'INCLUDE_NEW')))
                        ? SX_BOLD_API
                        : {}),
                      width: showSource ? '24%' : '30%',
                    }}
                  >
                    {diff.apiValue || '-'}
                  </TableCell>
                  <TableCell
                    sx={{
                      ...(mergeMode === 'selective' &&
                      (fieldSel === 'manual' ||
                        (!isShared && shipSel === 'KEEP_MANUAL'))
                        ? SX_BOLD_MANUAL
                        : {}),
                      width: showSource ? '24%' : '30%',
                    }}
                  >
                    {diff.manualValue || '-'}
                  </TableCell>
                  <TableCell
                    align='center'
                    sx={{ width: showSource ? '8%' : '15%' }}
                  >
                    {diff.different ? (
                      <WarningAmberIcon color='warning' fontSize='small' />
                    ) : (
                      <CheckCircleIcon color='success' fontSize='small' />
                    )}
                  </TableCell>
                  {showSource && (
                    <TableCell
                      align='center'
                      sx={{ width: '22%', whiteSpace: 'nowrap' }}
                    >
                      {isShared ? (
                        diff.different ? (
                          <RadioGroup
                            row
                            value={fieldSel || 'manual'}
                            onChange={(e) =>
                              onShipmentFieldSelectionChange(
                                marketId,
                                diff.fieldName,
                                e.target.value
                              )
                            }
                            sx={{
                              justifyContent: 'center',
                              flexWrap: 'nowrap',
                            }}
                          >
                            <FormControlLabel
                              value='api'
                              control={<Radio size='small' sx={SX_RADIO_API} />}
                              label={
                                <Typography variant='caption'>API</Typography>
                              }
                              sx={{ mr: 1, ml: 0 }}
                            />
                            <FormControlLabel
                              value='manual'
                              control={
                                <Radio size='small' sx={SX_RADIO_MANUAL} />
                              }
                              label={
                                <Typography variant='caption'>
                                  Manual
                                </Typography>
                              }
                              sx={{ ml: 0 }}
                            />
                          </RadioGroup>
                        ) : (
                          <Typography variant='caption' color='text.disabled'>
                            Same
                          </Typography>
                        )
                      ) : null}
                    </TableCell>
                  )}
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
};

export default StagingDiffViewer;
